import { DeviceCommand, SrBuildingCompany, SrDevice, SrEvacuationHeader, SrEvacuationSummary, SrMarshalPhone, SrPhoneInvite, SrStaff } from '@cloudsign/shared';
import { z } from 'zod';
import { useCaps } from '../../lib/session';

export {
  SrBuilding,
  SrBuildingCompany,
  SrBuildingMe,
  SrDevice,
  SrEvacuationHeader,
  SrEvacuationSummary,
  SrEvacuationView,
  SrMarkResult,
  SrMarshalPhone,
  SrPairingCode,
  SrRollCallRow,
  SrStaff,
  SrTotals,
} from '@cloudsign/shared';

/** The building area's routes (contract s5 short forms read as /shared-reception/building/...). */
export const BASE = '/shared-reception/building';

export const CompanyArray = z.array(SrBuildingCompany);
export const StaffArray = z.array(SrStaff);
export const DeviceArray = z.array(SrDevice);
export const CommandArray = z.array(DeviceCommand);
export const PhoneArray = z.array(SrMarshalPhone);
export const SummaryArray = z.array(SrEvacuationSummary);
export const StartAnswer = z.object({ header: SrEvacuationHeader, alreadyActive: z.boolean() });

/**
 * The invitation answer. The shared shape carries only the masked address (the server emails the link). If the server also
 * returns the link itself, the page shows it with a QR code; anything that is not a /c/srm_ link is ignored.
 */
export const InviteAnswer = SrPhoneInvite.extend({ link: z.string().optional() });
export type InviteAnswer = z.infer<typeof InviteAnswer>;
export const INVITE_LINK = /^https:\/\/[A-Za-z0-9.-]+(:\d+)?\/c\/srm_[A-Za-z0-9_-]{16,}$/;

export const keys = {
  me: ['building', 'me'] as const,
  companies: ['building', 'companies'] as const,
  staff: ['building', 'staff'] as const,
  devices: ['building', 'devices'] as const,
  commands: (id: string) => ['building', 'commands', id] as const,
  phones: ['building', 'phones'] as const,
  active: ['building', 'evacuation', 'active'] as const,
  history: ['building', 'evacuation', 'history'] as const,
};

/** The signed-in person's building role (the server checks it again on every call). */
export function useBuildingRole(): 'building_admin' | 'building_marshal' | null {
  return useCaps().building?.role ?? null;
}

export function useBuildingZone(): string {
  return useCaps().building?.timeZone ?? 'Europe/London';
}

export const statusTone = (s: string): 'neutral' | 'good' | 'warn' | 'bad' =>
  s === 'active' ? 'good' : s === 'paused' || s === 'leaving' || s === 'invited' ? 'warn' : 'neutral';
